/** Linking the preview and the timeline: reveal a clip's placement in the other view. */

/**
 * Horizontal scroll offset that brings a clip into the timeline's visible clip area.
 *
 * Clips live to the right of the sticky label column, which overlays the content, so the
 * usable band starts `labelWidth` past the current scroll offset. Returns the current
 * offset unchanged when the clip is already comfortably in view.
 */
export function timelineRevealScrollLeft({
  startTime = 0,
  duration = 0,
  pxPerSec = 80,
  viewportWidth = 0,
  labelWidth = 0,
  scrollLeft = 0,
  margin = 48,
}) {
  const perSec = Math.max(1, Number(pxPerSec) || 0);
  const clipLeft = labelWidth + Math.max(0, Number(startTime) || 0) * perSec;
  const clipWidth = Math.max(0, Number(duration) || 0) * perSec;
  const current = Math.max(0, Number(scrollLeft) || 0);
  const viewport = Math.max(0, Number(viewportWidth) || 0);
  // Park the clip start just right of the label column, the same spot every time.
  const parked = Math.max(0, clipLeft - labelWidth - margin);

  if (viewport <= labelWidth) return parked;

  const bandStart = current + labelWidth + margin;
  const bandEnd = current + viewport - margin;
  const fullyVisible = clipLeft >= bandStart && clipLeft + clipWidth <= bandEnd;
  return fullyVisible ? current : parked;
}

/**
 * Playhead time that guarantees a clip is on screen in the preview.
 * The midpoint avoids boundary rounding at the clip's first frame.
 */
export function clipRevealSeekTime(clip) {
  const start = Math.max(0, Number(clip?.startTime) || 0);
  const duration = Math.max(0, Number(clip?.duration) || 0);
  return start + duration / 2;
}

/** Index of the lane holding a clip, or -1. */
export function laneIndexOfClip(tracks, clipId) {
  if (!clipId || !Array.isArray(tracks)) return -1;
  return tracks.findIndex((lane) => (Array.isArray(lane) ? lane : []).some((clip) => clip?.id === clipId));
}
